<template>
  <div v-if="isOpen" class="dialog-overlay" @click="handleOverlayClick">
    <div class="dialog-container" @click.stop>

      <!-- Header -->
      <div class="dialog-header">
        <div class="dialog-title-wrap">
          <svg class="title-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
          <h2 class="dialog-title">Invite Member</h2>
        </div>
        <button @click="handleClose" class="close-button" aria-label="Close">
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" class="w-5 h-5">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Body -->
      <div class="dialog-body">

        <!-- ── Success state ── -->
        <div v-if="successUser" class="success-state">
          <div class="success-icon-wrap">
            <svg class="success-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 class="success-title">Invitation sent!</h3>
          <p class="success-body">
            <strong>{{ successUser }}</strong> will receive a notification and can accept or decline from their account.
          </p>
          <button @click="resetAndClose" class="btn btn-primary mt-4 w-full">Done</button>
        </div>

        <!-- ── Normal state ── -->
        <template v-else>
          <!-- Search input -->
          <div class="form-group">
            <label for="user-search">Search by name, username, or email</label>
            <div class="search-input-wrapper">
              <input
                id="user-search"
                v-model="searchQuery"
                @input="handleSearch"
                type="text"
                placeholder="e.g. victor or victor@example.com"
                :disabled="isSending"
                class="search-input"
                autocomplete="off"
              />
              <div v-if="isSearching" class="search-spinner">
                <div class="spinner-small"></div>
              </div>
            </div>

            <!-- Results dropdown -->
            <div v-if="searchResults.length > 0 && !selectedUser" class="search-results">
              <div
                v-for="user in searchResults"
                :key="user.id"
                @click="selectUser(user)"
                class="search-result-item"
              >
                <div class="user-avatar">{{ initials(user) }}</div>
                <div>
                  <div class="user-name">{{ name(user) }}</div>
                  <div class="user-email">{{ user.email }}</div>
                </div>
              </div>
            </div>

            <!-- No results -->
            <div
              v-else-if="searchQuery.length >= 2 && !isSearching && searchPerformed && searchResults.length === 0"
              class="no-results"
            >
              No users found for "{{ searchQuery }}"
            </div>
          </div>

          <!-- Selected user card -->
          <div v-if="selectedUser" class="selected-user">
            <div class="selected-user-header">
              <span>Inviting</span>
              <button @click="clearSelection" class="clear-btn" title="Change user">×</button>
            </div>
            <div class="user-info">
              <div class="user-avatar">{{ initials(selectedUser) }}</div>
              <div>
                <div class="user-name">{{ name(selectedUser) }}</div>
                <div class="user-email">{{ selectedUser.email }}</div>
              </div>
            </div>
          </div>

          <!-- Error message -->
          <div v-if="errorMessage" class="error-message">
            <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
            <span>{{ errorMessage }}</span>
          </div>

          <!-- Info note -->
          <p class="info-note">
            The user will receive a notification and must accept the invitation before joining the group.
          </p>
        </template>
      </div>

      <!-- Footer (hidden in success state) -->
      <div v-if="!successUser" class="dialog-footer">
        <button @click="handleClose" class="btn btn-secondary" :disabled="isSending">
          Cancel
        </button>
        <button
          @click="sendInvitation"
          class="btn btn-primary"
          :disabled="!selectedUser || isSending"
        >
          <span v-if="isSending" class="spinner"></span>
          {{ isSending ? 'Sending…' : 'Send Invitation' }}
        </button>
      </div>

    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { debounce } from 'lodash-es'
import { userService, displayName, type UserSearchResult } from '../../services/userService'
import { groupService } from '../../services/groupService'

const props = defineProps<{
  isOpen: boolean
  groupId: number
  existingMemberIds?: number[]
}>()

const emit = defineEmits<{
  close: []
  invited: []      // emitted after a successful invitation so parent can react if needed
}>()

// ── State ────────────────────────────────────────────────────────────────────
const searchQuery   = ref('')
const searchResults = ref<UserSearchResult[]>([])
const selectedUser  = ref<UserSearchResult | null>(null)
const isSearching   = ref(false)
const isSending     = ref(false)
const searchPerformed = ref(false)
const errorMessage  = ref('')
const successUser   = ref('')   // name shown in success state

// ── Helpers ───────────────────────────────────────────────────────────────────
function name(u: UserSearchResult) { return displayName(u) }

function initials(u: UserSearchResult): string {
  const n = displayName(u)
  const parts = n.split(' ')
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return n.substring(0, 2).toUpperCase()
}

// ── Search ────────────────────────────────────────────────────────────────────
const handleSearch = debounce(async () => {
  searchResults.value = []
  searchPerformed.value = false
  if (searchQuery.value.length < 2) return

  isSearching.value = true
  errorMessage.value = ''
  try {
    const results = await userService.searchUsers(searchQuery.value)
    // Filter out users already in the group
    searchResults.value = results.filter(
      u => !props.existingMemberIds?.includes(u.id)
    )
    searchPerformed.value = true
  } catch {
    errorMessage.value = 'Could not search users. Please try again.'
  } finally {
    isSearching.value = false
  }
}, 300)

function selectUser(u: UserSearchResult) {
  selectedUser.value = u
  searchQuery.value = ''
  searchResults.value = []
  searchPerformed.value = false
  errorMessage.value = ''
}

function clearSelection() {
  selectedUser.value = null
  errorMessage.value = ''
}

// ── Send invitation ───────────────────────────────────────────────────────────
async function sendInvitation() {
  if (!selectedUser.value) return
  isSending.value = true
  errorMessage.value = ''

  try {
    await groupService.sendGroupInvitation(props.groupId, selectedUser.value.id)
    successUser.value = name(selectedUser.value)
    emit('invited')
  } catch (err: unknown) {
    const e = err as { status?: number; data?: { error?: { code?: string } }; message?: string }
    const code = e?.data?.error?.code ?? ''

    if (code === 'INVITATION_ALREADY_EXISTS') {
      errorMessage.value = 'An invitation has already been sent to this user and is still pending.'
    } else if (code === 'INVITEE_ALREADY_MEMBER') {
      errorMessage.value = 'This user is already a member of the group.'
    } else if (code === 'CANNOT_INVITE_SELF') {
      errorMessage.value = 'You cannot invite yourself to a group.'
    } else if (e?.status === 403) {
      errorMessage.value = "You don't have permission to invite members to this group."
    } else {
      errorMessage.value = e?.message || 'Failed to send invitation. Please try again.'
    }
  } finally {
    isSending.value = false
  }
}

// ── Close / reset ─────────────────────────────────────────────────────────────
function resetForm() {
  searchQuery.value = ''
  searchResults.value = []
  selectedUser.value = null
  isSearching.value = false
  isSending.value = false
  searchPerformed.value = false
  errorMessage.value = ''
  successUser.value = ''
}

function handleClose() {
  if (!isSending.value) {
    emit('close')
  }
}

function resetAndClose() {
  resetForm()
  emit('close')
}

function handleOverlayClick() {
  if (!isSending.value) emit('close')
}

watch(() => props.isOpen, (open) => {
  if (!open) resetForm()
})
</script>

<style scoped>
.dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

.dialog-container {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 480px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 25px -5px rgba(0,0,0,.1), 0 10px 10px -5px rgba(0,0,0,.04);
  animation: slideUp 0.25s cubic-bezier(0.4,0,0.2,1);
  overflow: hidden;
}

@keyframes slideUp {
  from { transform: translateY(16px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}

/* Header */
.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.dialog-title-wrap {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.title-icon {
  width: 1.25rem;
  height: 1.25rem;
  color: #3b82f6;
  flex-shrink: 0;
}

.dialog-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
}

.close-button {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 6px;
  display: flex;
  transition: all 0.15s;
}

.close-button:hover { background: #f3f4f6; color: #374151; }

/* Body */
.dialog-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.form-group {
  position: relative;
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.search-input-wrapper { position: relative; }

.search-input {
  width: 100%;
  padding: 0.625rem 2.5rem 0.625rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.15s;
  box-sizing: border-box;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59,130,246,.15);
}

.search-input:disabled { background: #f9fafb; cursor: not-allowed; }

.search-spinner {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
}

.spinner-small {
  width: 1rem;
  height: 1rem;
  border: 2px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

/* Results dropdown */
.search-results {
  position: absolute;
  top: calc(100% + 4px);
  left: 0; right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0,0,0,.1);
  max-height: 220px;
  overflow-y: auto;
  z-index: 10;
}

.search-result-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  cursor: pointer;
  transition: background 0.15s;
}

.search-result-item:hover { background: #f0f9ff; }
.search-result-item + .search-result-item { border-top: 1px solid #f3f4f6; }

.no-results {
  padding: 0.75rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
}

/* User avatar / info */
.user-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.user-avatar {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  background: #eff6ff;
  color: #3b82f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.8125rem;
  flex-shrink: 0;
}

.user-name { font-size: 0.875rem; font-weight: 500; color: #111827; }
.user-email { font-size: 0.75rem; color: #6b7280; margin-top: 1px; }

/* Selected user card */
.selected-user {
  background: #f0f9ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 0.875rem 1rem;
  margin-bottom: 1.25rem;
}

.selected-user-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.625rem;
}

.selected-user-header span {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #2563eb;
}

.clear-btn {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
  padding: 0 0.25rem;
  border-radius: 4px;
  transition: all 0.15s;
}

.clear-btn:hover { background: #dbeafe; color: #1d4ed8; }

/* Error */
.error-message {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  color: #dc2626;
  font-size: 0.875rem;
  padding: 0.75rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  margin-bottom: 1rem;
}

/* Info note */
.info-note {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0;
  padding-top: 0.5rem;
}

/* Success state */
.success-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1rem 0;
}

.success-icon-wrap {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  background: #dcfce7;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.success-icon {
  width: 1.75rem;
  height: 1.75rem;
  color: #16a34a;
  stroke-width: 2.5;
}

.success-title {
  margin: 0 0 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
}

.success-body {
  margin: 0;
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.6;
  max-width: 320px;
}

/* Footer */
.dialog-footer {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.btn {
  padding: 0.625rem 1.25rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn.w-full { width: 100%; }
.btn.mt-4 { margin-top: 1rem; }

.btn:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-primary { background: #3b82f6; color: white; }
.btn-primary:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59,130,246,.35);
}

.btn-secondary { background: #f3f4f6; color: #374151; }
.btn-secondary:hover:not(:disabled) { background: #e5e7eb; }

.spinner {
  width: 0.875rem;
  height: 0.875rem;
  border: 2px solid rgba(255,255,255,.35);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 640px) {
  .dialog-container { width: 95%; }
  .dialog-header, .dialog-body, .dialog-footer { padding: 1rem; }
  .dialog-footer { flex-direction: column-reverse; }
  .dialog-footer .btn { width: 100%; }
}
</style>
